<script lang="ts">
	import ModernBackground from '#lib/modern/ModernBackground.svelte';
	import Portal from '#lib/modern/Portal.svelte';
	import { confirmationState } from '#lib/stores/confirmation.js';
	import { t } from '#lib/stores/i18n.js';
	import type { Snippet } from 'svelte';
	import { onDestroy, onMount } from 'svelte';
	import { fade, fly } from 'svelte/transition';

	interface Props {
		children: Snippet;
		canSkip?: boolean;
		skipDescription?: string;
		maxWidthClass?: string;
		onclose?: () => void;
		close?: () => void;
	}

	let {
		children,
		canSkip = false,
		skipDescription = '',
		maxWidthClass = 'max-w-3xl',
		onclose = () => {},
		close = $bindable()
	}: Props = $props();

	let show = $state(false);
	let mountTimer: ReturnType<typeof setTimeout> | null = null;
	let closeTimer: ReturnType<typeof setTimeout> | null = null;

	onDestroy(() => {
		if (mountTimer) clearTimeout(mountTimer);
		if (closeTimer) clearTimeout(closeTimer);
	});

	onMount(() => {
		mountTimer = setTimeout(() => (show = true), 200);
	});

	function _onclose(): void {
		if (!show) return;
		show = false;
		if (closeTimer) clearTimeout(closeTimer);
		closeTimer = setTimeout(onclose, 400);
	}

	$effect(() => {
		close = _onclose;
	});
</script>

<svelte:window
	onkeydown={(e) => {
		if ($confirmationState.show) return;
		if (e.key === 'Escape' && canSkip) {
			_onclose();
		}
	}}
/>

<Portal>
	{#if show}
		<div
			class="fixed inset-0 z-50 backdrop-blur-md bg-black/60 dark:bg-black/75 overflow-hidden"
			transition:fade={{ duration: 150 }}
		>
			<div class="w-full h-full sm:opacity-25 opacity-35">
				<ModernBackground />
			</div>
		</div>

		<div
			class="fixed inset-0 z-50 w-full h-full overflow-y-auto"
			transition:fly={{ y: 20, duration: 400 }}
			role="presentation"
			onclick={(e) => {
				if (canSkip && e.target === e.currentTarget) {
					_onclose();
				}
			}}
		>
			<div
				class="{maxWidthClass} mx-auto sm:p-8 px-4"
				role="presentation"
				onclick={(e) => {
					if (canSkip && e.target === e.currentTarget) {
						_onclose();
					}
				}}
			>
				{#if canSkip}
					<div
						class="w-full text-center text-sm flex flex-col text-surface/80 p-4 sm:pt-0"
					>
						{#if skipDescription}
							<button class="mx-auto" onclick={() => _onclose()}>
								{$t('form.skip')}
							</button>
							<div class="opacity-50">
								{skipDescription}
							</div>
						{/if}
					</div>
				{/if}
				{@render children()}
			</div>
		</div>
	{/if}
</Portal>
